<template>
	<v-container v-bind="$attrs" v-on="$listeners">

		<PaymentProcessOverlay
			:loading="savingInvoice"
		/>

		<v-row>
			<v-col cols="12" md="6">
				<PaymentForm
					:cart.sync="cart"
					:payments.sync="payments"
					:skeleton="skeleton || loading"
					@completed="handlePaymentCompleted"
					@partialPaymentCompleted="handlePartialPaymentCompleted"/>
			</v-col>
			<v-col cols="12" md="6">
				<RebateCard :cart.sync="cart" :skeleton="skeleton" class="mb-8" />
				<SummaryOrderCard
					:cart.sync="cart"
					:payments.sync="payments"
					:skeleton="skeleton || loading"
					:load="load"
					:showContinueButton="checkFullDiscount"
					:show-cart-payments="true"
					@partialPaymentCompleted="handlePartialPaymentCompleted"/>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
	import PaymentForm from "@/components/PaymentForm";
	import RebateCard from "@/components/RebateCard";
	import PaymentProcessOverlay from "@/components/PaymentProcessOverlay";
	import SummaryOrderCard from "@/components/SummaryOrderCard";
	import {
		CartModel,
		EComService,
		PaymentModel,
		EventBus,
	} from "@connectngo/sdk";
  import ReloadV2Mixin from '@/mixins/ReloadV2Mixin'
  import globalVariables from '@/global'

	export default {
		name: "Payment",

		components: {
			PaymentForm,
			RebateCard,
			SummaryOrderCard,
			PaymentProcessOverlay,
		},

    mixins : [ReloadV2Mixin],

		props: {
			cart: {
				type: CartModel,
				default: () => new CartModel(),
			},
			payments: {
				type: PaymentModel,
				default: () => new PaymentModel(),
			},
			loading: {
				type: Boolean,
				default: false,
			},
		},

		data: () => ({
			savingInvoice: false,
			saveInvoicePromise: null,
			skeleton: true,
		}),

		computed: {
			_cart: {
				get() {
					return this.cart;
				},
				set(value) {
					this.$emit("update:cart", value);
				},
			},
			dev11517RedirectFlowFixFfEnabled() {
				const { feature_flags } = this.$root?.websiteConfig?.data || {};
				return !!(feature_flags && feature_flags.includes('dev-11517-fix-redirect-flow-for-ecom-payments'));
			},
			checkFullDiscount: function () {
				return (
					Object.keys(this.cart?.getAppliedDiscounts())?.length > 0 &&
					this.cart.data?.total <= 0
				);
			},
			isFFEcomAccountRequired() {
				const { feature_flags } = this.$root.websiteConfig?.data || {};
				return feature_flags?.includes('dev-11081-ecom-account-required') ?? false;
			},
		},

		methods: {
			/* Google Tag Manager */
			triggerGAEvent(invoice) {
				if (this.$gtm) {
					const items = [];
					this.cart.data.items.forEach((item, index) => {
						items.push({
							item_id: item.data.product.data.id,
							item_name: this.$options.filters.translatable(
								item.data.product.data.name,
								item.data.product.data.name_i18n,
								this.$i18n.locale
							),
							coupon: item.data.hasDiscount ?
								this.$options.filters.translatable(
									item.data.discount.name,
									item.data.discount.name_i18n,
									this.$i18n.locale
								)
								: null,
							currency:
								this.$root.websiteConfig.data.tenant.currency.code,
							discount: item.data.discountTotal,
							index: index++,
							item_category: item.data.mainTag ?
								this.$options.filters.translatable(
									item.data.mainTag.name,
									item.data.mainTag.name_i18n,
									this.$i18n.locale
								)
								: null,
							price: item.data.unitPrice,
							quantity: item.data.quantity,
						});
					});

					dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

					dataLayer.push({
						event: "purchase",
						tenant_id: this.$root.websiteConfig.data.tenant.id,
						ecommerce: {
							transaction_id: invoice.data.invoice.transaction_id,
							value: parseFloat(invoice.data.invoice.amount),
							currency:
								this.$root.websiteConfig.data.tenant.currency.code,
							items: items,
						},
					});
				}
			},

			handlePaymentCompleted(response) {
				this.saveInvoice(response);
			},

			handlePartialPaymentCompleted() {
				this.$emit('partialPaymentCompleted')
			},
			saveInvoice(response = null) {
				if (this.dev11517RedirectFlowFixFfEnabled && this.saveInvoicePromise) {
					return this.saveInvoicePromise;
				}

				const cartId = response?.data?.uuid || this.cart.data.id;

				this.savingInvoice = true;

				const promise = new EComService()
					.saveInvoice(cartId)
					.then((invoice) => {
						this.triggerGAEvent(invoice);
						return new EComService().createCart().then((cart) => {
							this.$root.cart = cart;
                				globalVariables.cart = cart;
							EventBus.publish("CART_UPDATED", cart);
							this.$router.push({
								name: "invoice_step",
								params: {
									uuid: invoice.data.invoice.uuid,
									step: "confirmation",
								},
							});
						});
					})
					.catch((error) => {
						this.saveInvoicePromise = null;
						this.$handleError(this, error);
					})
					.finally(() => (this.savingInvoice = false));

				if (this.dev11517RedirectFlowFixFfEnabled) {
					this.saveInvoicePromise = promise;
				}

				return promise;
			},
			load() {
				if (!this.$root.user.data.id) {
					this.$router.push({ name: "cart_step", params: { step: 'identification' }});
					return;
				}

				const afterPatch = () => {
					this.validateCart();

					if (
						(this.payments.data.balance <= 0 ||
						this.checkFullDiscount) &&
						0 < this.cart.data.items.length
					) {
						this.saveInvoice().then(() => (this.skeleton = false));
					} else {
						this.skeleton = false;
					}
				};

				if (this.isFFEcomAccountRequired) {
					this.patchCartAccountId().then(afterPatch);
				} else {
					this.patchCartAccountId();
					afterPatch();
				}
			},
			patchCartAccountId() {
				return new EComService()
					.patchCart({ account_id: this.$root.user.data.id })
					.then((data) => {
						this.$root.cart = data;
					})
					.catch((e) => {
						this.$handleError(this, e);
					});
			},
			validateCart() {
				new EComService()
					.validateCart()
					.catch((e) => {
						if (new EComService().isMissingRequiredFields(e)) {
							EventBus.publish('MISSING_ACCOUNT_FIELDS', e.fields);
						} else {
							this.$handleError(this, e);
						}
					});
			}
		},

		created() {
			if (!this.allReloadableAssigned) {
				// If reload items without wallet attached redirect to cart summary
				this.$router.push({ name: 'cart' })
				return;
			}
			this.load();
		},
	};
</script>
